import type { BillingPlanDto } from '@shared/core/billing'
import { useMemo } from 'react'
import { roundNumber } from 'src/modules/core/utils/helpers'
import { billingTypes, SHORT_BILLING_INTERVAL } from 'src/modules/payment/constants/payment.constants'
import { usePaddlePlanData } from 'src/modules/payment/hooks/use-paddle-plan-data'
import {
  getLocalizedPaddlePlanFullPrice,
  getLocalizedPaddlePlanPrice,
} from 'src/modules/payment/utils/get-localized-paddle-plan-price'
import type { PlanOfferPrice } from './plan-offer-card.interface'

const DAYS_IN_WEEK = 7
const MONTHS_IN_YEAR = 12

/** Live Paddle price localized to the owner, with the stored plan price only until Paddle answers. */
export function usePlanOfferPrice(plan: BillingPlanDto): PlanOfferPrice {
  const paddlePlanData = usePaddlePlanData({ planId: plan.paddle_id })
  const isLongInterval = plan.billing_type === billingTypes.longInterval

  return useMemo(() => {
    if (paddlePlanData.data) {
      return {
        price: getLocalizedPaddlePlanPrice(paddlePlanData.data),
        billedYearlyPrice: isLongInterval ? getLocalizedPaddlePlanFullPrice(paddlePlanData.data) : null,
      }
    }

    const divisor = SHORT_BILLING_INTERVAL ? DAYS_IN_WEEK : MONTHS_IN_YEAR
    const amount = isLongInterval ? roundNumber(plan.price / divisor, 2) : plan.price

    return { price: `$${amount}`, billedYearlyPrice: isLongInterval ? `$${plan.price}` : null }
  }, [isLongInterval, paddlePlanData.data, plan.price])
}
